import {
  Children,
  cloneElement,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { getConfirmLabel, useTokenValues } from '@jds/react-engine';
import {
  getDisabledYears,
  getNormalisedDate,
  loadedMonths,
  loadedYears,
  months
} from '../jdsdatepickerdesktop/dateLogic';

import { JDSDatepickerDesktopInternalProps } from './generated';

const YEAR_COUNT = 50;

export interface DatePickerInternalProps
  extends Omit<
    JDSDatepickerDesktopInternalProps,
    | 'state'
    | 'monthName'
    | 'year'
    | 'offsetX'
    | 'offsetY'
    | 'onClose'
    | 'current'
    | 'months'
    | 'yearData'
    | 'onMonthSelect'
    | 'onYearSelect'
    | 'onPrev'
    | 'onNext'
    | 'onSelect'
  > {
  triggerRef?: React.MutableRefObject<HTMLElement>;
  onClose?: () => void;
  value?: string | Date;
  min?: string | Date;
  max?: string | Date;
  onMonthSelect?: () => void;
  onYearSelect?: () => void;
  /**
   * @deprecated use `onSelect` instead
   */
  onChange?: (value: Date) => void;
  onSelect?: (value: Date) => void;
  dateFilter?: (date: string | Date) => boolean;
  disabledDates?: Array<string | Date>;
  onConfirm?: (value: Date) => void;
  position?:
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'right-start'
    | 'right-end'
    | 'top'
    | 'left-start'
    | 'left-end'
    | 'bottom';
  children?: JSX.Element;
  /**
   * @deprecated use open prop
   */
  close?: boolean;
}

export const withLogic =
  (
    Component: React.ComponentType<DatePickerInternalProps>
  ): React.FC<DatePickerInternalProps> =>
  (props: DatePickerInternalProps) => {
    const { yearSelectable = true, monthSelectable = true, ...rest } = props;

    const { lang, platform } = useTokenValues();

    const confirmLabelLocal = useMemo(() => {
      return getConfirmLabel(lang);
    }, [lang]);

    const [state, setState] = useState('default');
    const anchorRef = useRef<HTMLElement>(null);

    const { day, month, year } = getNormalisedDate(props.value);
    const [selectedMonth, setSelectedMonth] = useState(month);
    const [selectedDate, setSelectedDate] = useState({
      date: day,
      month,
      year
    });
    const [selectedYear, setSelectedYear] = useState(year);

    useEffect(() => {
      const { day, month, year } = getNormalisedDate(props.value);
      setSelectedDate({ date: day, month, year });
      setSelectedYear(year);
      setSelectedMonth(month);
    }, [props.value]);

    const [firstChild = <span></span>, ...restChildren] = Children.toArray(
      props.children
    );
    const formattedConfirmDate = new Date(
      selectedDate.year,
      selectedDate.month - 1,
      selectedDate.date
    ).toLocaleDateString(lang, {
      weekday: 'short',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    return (
      <>
        <Component
          {...{
            ...rest,
            onClose: () => {
              props.onClose?.();
              setState('default');
            },
            yearSelectable,
            monthSelectable,
            confirmDate: formattedConfirmDate,
            _confirmLabel: confirmLabelLocal,
            value: new Date(
              selectedDate.year,
              selectedDate.month - 1,
              selectedDate.date
            ),
            onClick: (ev: React.MouseEvent<HTMLDivElement>) =>
              ev.stopPropagation(),
            date: selectedDate.date,
            month: selectedMonth,
            year: selectedYear,
            monthName: selectedMonth - 1,
            months: loadedMonths(
              selectedMonth - 1,
              selectedYear,
              monthSelectable,
              props.calendarData?.min,
              props.calendarData?.max
            ),
            loadedYears: loadedYears(
              selectedYear + YEAR_COUNT / 2,
              selectedYear - YEAR_COUNT / 2,
              selectedYear,
              getDisabledYears(
                selectedYear - YEAR_COUNT / 2,
                selectedYear + YEAR_COUNT / 2,
                props.calendarData?.min,
                props.calendarData?.max
              )
            ),
            current: selectedYear,
            state,

            onConfirm: () => {
              const date = new Date(
                selectedDate.year,
                selectedDate.month - 1,
                selectedDate.date
              );
              props.onSelect?.(date);
              props.onConfirm?.(date);
              props?.onClose?.();
            },
            _onYearClick: () => setState('year_view'),
            _onMonthClick: () => setState('month_view'),
            _onBack: () => setState('default'),
            _onSelect: (num: number) => {
              setSelectedDate({
                date: num,
                month: selectedMonth,
                year: selectedYear
              });
              setState('default');
            },
            onSelect: (num: number, disabled?: boolean) => {
              setSelectedDate({
                date: num,
                month: selectedMonth,
                year: selectedYear
              });
              if (!disabled && platform === 'desktop') {
                props.onSelect?.(
                  new Date(selectedYear, selectedMonth - 1, num)
                );
                props.onClose?.();
              }
              setState('default');
            },
            _onMonthSelect: (i: number) => {
              if (monthSelectable) {
                setSelectedMonth(i);
                setState('default');
              }
            },
            _onYearSelect: (i: number) => {
              if (yearSelectable) {
                setSelectedYear(i);
                setState('default');
              }
            },
            _onPrev: () =>
              setSelectedMonth((m) => {
                if (m === 1) {
                  setSelectedYear(selectedYear - 1);
                  return 12;
                }
                return m - 1;
              }),
            _onNext: () =>
              setSelectedMonth((m) => {
                if (m === 12) {
                  setSelectedYear(selectedYear + 1);
                  return 1;
                }
                return m + 1;
              })
          }}
        />
        {cloneElement(firstChild as React.ReactElement, { ref: anchorRef })}
        {restChildren}
      </>
    );
  };
